// ============================================================================
// PairsForm — Catalogue officiel des clés de permission (Sprint 8 · Étape B1)
// ============================================================================
// Source unique de vérité pour les clés de permission reconnues par le
// système. Partagé entre le backend (middleware requirePermission) et le
// frontend (hook useCan) pour éviter les fautes de frappe.
//
// Règles de nommage :
//   - Format : <DOMAINE>.<ressource>.<action>
//   - DOMAINE en MAJUSCULES, ressource et action en snake_case ASCII.
//   - Une clé par fonctionnalité métier significative, pas une par endpoint.
//
// Refus par défaut : une clé absente de la matrice signifie "pas autorisé".
// Les routes publiques définitives (cf. docs/PERMISSIONS_RECENSEMENT.md §4.1)
// ne sont PAS dans ce catalogue ; elles sont verrouillées en code.
// ============================================================================
/**
 * Domaines de permissions. Chaque domaine regroupe un ensemble cohérent de
 * fonctionnalités du produit. L'UI admin des permissions présentera les
 * clés par domaine.
 */
const PERMISSION_DOMAINS = {
    SELF: "Mon espace personnel",
    INTRA: "Projets et groupes Intra",
    INTER: "Plateforme Inter (marketplace)",
    ANIMATEUR: "Espace animateur / formateur",
    PARTICIPANT: "Espace participant / stagiaire",
    ADMIN: "Administration Cecodev",
    SUPPORT: "Support et contact",
    QUALIOPI: "Suivi Qualiopi et veille",
    CERTIFICATION: "Jury et bilans de certification",
    OUTILS_IA: "Outils IA avancés",
    CRM: "CRM organisations clientes",
};
/**
 * Catalogue officiel. À étendre dans la PR qui ajoute la fonctionnalité
 * correspondante — ne jamais renommer une clé déjà utilisée en base sans
 * migration de données.
 */
export const PERMISSIONS = [
    // -------- SELF --------
    // -------- INTRA --------
    { key: "INTRA.projet.list", domain: "INTRA", label: "Lister les projets de l'organisation" },
    { key: "INTRA.projet.create", domain: "INTRA", label: "Créer un projet" },
    { key: "INTRA.projet.edit", domain: "INTRA", label: "Modifier un projet" },
    { key: "INTRA.projet.delete", domain: "INTRA", label: "Archiver un projet" },
    { key: "INTRA.groupe.create", domain: "INTRA", label: "Créer un groupe dans un projet" },
    { key: "INTRA.groupe.edit", domain: "INTRA", label: "Modifier un groupe" },
    { key: "INTRA.groupe.delete", domain: "INTRA", label: "Supprimer / archiver un groupe" },
    { key: "INTRA.seance.create", domain: "INTRA", label: "Créer une séance" },
    { key: "INTRA.emargement.gerer", domain: "INTRA", label: "Gérer les émargements d'une séance" },
    // -------- INTER --------
    { key: "INTER.groupe.proposer", domain: "INTER", label: "Proposer un nouveau groupe Inter" },
    { key: "INTER.proposition.valider", domain: "INTER", label: "Valider / refuser une proposition de groupe" },
    { key: "INTER.inscription.creer", domain: "INTER", label: "S'inscrire à un groupe Inter" },
    { key: "INTER.dashboard_admin.view", domain: "INTER", label: "Consulter le dashboard pilotage Inter" },
    { key: "INTER.reglages.gerer", domain: "INTER", label: "Configurer les tarifs et cycles Inter" },
    // -------- ANIMATEUR --------
    { key: "ANIMATEUR.dashboard.view", domain: "ANIMATEUR", label: "Consulter mon dashboard animateur" },
    { key: "ANIMATEUR.groupes.view", domain: "ANIMATEUR", label: "Consulter mes groupes (Inter + Intra)" },
    { key: "ANIMATEUR.documents.uploader", domain: "ANIMATEUR", label: "Déposer mes documents administratifs" },
    { key: "ANIMATEUR.documents.consulter", domain: "ANIMATEUR", label: "Consulter mes documents" },
    { key: "ANIMATEUR.statut.consulter", domain: "ANIMATEUR", label: "Consulter mon statut administratif" },
    { key: "ANIMATEUR.contrat_cadre.signer", domain: "ANIMATEUR", label: "Signer mon contrat-cadre" },
    { key: "ANIMATEUR.lettre_mission.accepter", domain: "ANIMATEUR", label: "Accepter une lettre de mission" },
    { key: "ANIMATEUR.facture.deposer", domain: "ANIMATEUR", label: "Déposer une facture de rémunération" },
    { key: "ANIMATEUR.revenus.consulter", domain: "ANIMATEUR", label: "Consulter mes revenus" },
    { key: "ANIMATEUR.bpf.consulter", domain: "ANIMATEUR", label: "Consulter ma synthèse BPF" },
    { key: "ANIMATEUR.entretien.realiser", domain: "ANIMATEUR", label: "Réaliser mon entretien annuel" },
    { key: "ANIMATEUR.export_qualiopi.telecharger", domain: "ANIMATEUR", label: "Exporter un ZIP Qualiopi d'un groupe" },
    { key: "ANIMATEUR.candidature.postuler", domain: "ANIMATEUR", label: "Postuler comme animateur sur la plateforme" },
    { key: "ANIMATEUR.bpf_export_perso.telecharger", domain: "ANIMATEUR", label: "Exporter mes sessions animées chez Cecodev pour mon BPF (sous-traitants)" },
    // -------- PARTICIPANT --------
    { key: "PARTICIPANT.mes_seances.view", domain: "PARTICIPANT", label: "Consulter mes séances" },
    { key: "PARTICIPANT.absence.declarer", domain: "PARTICIPANT", label: "Déclarer une absence avec justificatif" },
    { key: "PARTICIPANT.documents.consulter", domain: "PARTICIPANT", label: "Consulter mes documents (contrats, reçus, attestation)" },
    { key: "PARTICIPANT.mon_espace.view", domain: "PARTICIPANT", label: "Accéder à mon espace Inter" },
    // -------- ADMIN --------
    { key: "ADMIN.organisations.list", domain: "ADMIN", label: "Lister toutes les organisations" },
    { key: "ADMIN.organisations.edit", domain: "ADMIN", label: "Créer / éditer une organisation" },
    { key: "ADMIN.users.list", domain: "ADMIN", label: "Lister les utilisateurs d'une organisation" },
    { key: "ADMIN.users.edit", domain: "ADMIN", label: "Éditer un utilisateur (rôles, membership)" },
    { key: "ADMIN.absences.valider", domain: "ADMIN", label: "Valider ou refuser une déclaration d'absence" },
    { key: "ADMIN.candidatures_animateur.valider", domain: "ADMIN", label: "Valider une candidature d'animateur" },
    { key: "ADMIN.permissions.editer", domain: "ADMIN", label: "Modifier la matrice de permissions" },
    { key: "ADMIN.analytics.view", domain: "ADMIN", label: "Consulter les analytics globaux" },
    { key: "ADMIN.reporting_financier.view", domain: "ADMIN", label: "Consulter le reporting financier Cecodev" },
    { key: "ADMIN.reporting_financier.export", domain: "ADMIN", label: "Exporter le reporting financier (CSV)" },
    // -------- SUPPORT --------
    { key: "SUPPORT.toutes_demandes.consulter", domain: "SUPPORT", label: "Consulter toutes les demandes de support" },
    { key: "SUPPORT.demande.repondre", domain: "SUPPORT", label: "Répondre et clôturer une demande" },
    // -------- QUALIOPI --------
    { key: "QUALIOPI.indicateurs.consulter", domain: "QUALIOPI", label: "Consulter les indicateurs Qualiopi" },
    { key: "QUALIOPI.suivi.editer", domain: "QUALIOPI", label: "Mettre à jour le suivi Qualiopi" },
    { key: "QUALIOPI.veille.creer", domain: "QUALIOPI", label: "Enregistrer une veille (légale / compétences / pédago)" },
    { key: "QUALIOPI.actions.gerer", domain: "QUALIOPI", label: "Créer et suivre un plan d'actions Qualiopi" },
    { key: "QUALIOPI.bpf.consulter", domain: "QUALIOPI", label: "Consulter le BPF organisationnel (toutes les inscriptions)" },
    { key: "QUALIOPI.bpf.editer", domain: "QUALIOPI", label: "Saisir/éditer les données BPF d'une inscription" },
    // -------- CERTIFICATION --------
    { key: "CERTIFICATION.jury.acceder", domain: "CERTIFICATION", label: "Accéder à l'espace jury" },
    // -------- OUTILS IA --------
    { key: "OUTILS_IA.correcteur.utiliser", domain: "OUTILS_IA", label: "Utiliser le correcteur IA (RS 7137)" },
    { key: "OUTILS_IA.transcription.utiliser", domain: "OUTILS_IA", label: "Utiliser le transcripteur audio" },
    { key: "OUTILS_IA.bilan.generer", domain: "OUTILS_IA", label: "Générer un bilan assisté IA" },
    // Pilier recherche n°2 (PLAN_TRANSCRIPTEUR_RECHERCHE, D8) : réservé aux
    // animateurs CodevTeam — tout le métier Codev est dedans. Le seed de la
    // matrice ne l'accorde qu'à CECODEV × ANIMATEUR/CODEV_TEAM.
    { key: "OUTILS_IA.transcripteur_recherche.utiliser", domain: "OUTILS_IA", label: "Utiliser le transcripteur de séance (corpus recherche)" },
    // -------- CRM (feature F) --------
    { key: "CRM.notes.view", domain: "CRM", label: "Consulter les notes CRM d'une organisation" },
    { key: "CRM.notes.edit", domain: "CRM", label: "Créer, modifier et archiver des notes CRM" },
    // Chantier CRM C1 (FX 2026-08-12) — affaires commerciales (vue plateforme
    // Cecodev). Accordées automatiquement au pilote Cecodev via
    // keysByDomain("CRM") dans CECODEV_PILOTE_KEYS.
    { key: "CRM.affaires.view", domain: "CRM", label: "Consulter les affaires commerciales" },
    { key: "CRM.affaires.edit", domain: "CRM", label: "Créer et gérer les affaires commerciales" },
];
/** Set des clés valides — utilisé par le backend pour rejeter les clés inconnues. */
const PERMISSION_KEYS = new Set(PERMISSIONS.map((p) => p.key));
export function isKnownPermission(key) {
    return PERMISSION_KEYS.has(key);
}
//# sourceMappingURL=permissions.js.map